import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  getCookie, setCookie, handleAdminLogoutRedirect, Logger
} from '../../utilities';
import {
  BASE_API_URL,
  endpoints,
  ACCESS_TOKEN_AGE,
  REFRESH_TOKEN_AGE,
  ADMIN_TOKEN_COOKIE_NAME,
  ADMIN_REFRESH_TOKEN_COOKIE_NAME
} from '../../constants';

const baseQuery = fetchBaseQuery({
  baseUrl: BASE_API_URL,
  credentials: 'same-origin',
  prepareHeaders: (headers) => {
    const token = getCookie(ADMIN_TOKEN_COOKIE_NAME);
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }
});

let adminRefreshPromise = null;

const attemptAdminTokenRefresh = async () => {
  const refreshToken = getCookie(ADMIN_REFRESH_TOKEN_COOKIE_NAME);
  if (!refreshToken) return null;
  try {
    const response = await fetch(`${BASE_API_URL}${endpoints.admin.refresh}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken })
    });
    return response.ok ? response.json() : null;
  } catch {
    return null;
  }
};

const SKIP_REFRESH_URLS = [endpoints.admin.login, endpoints.admin.refresh];

const baseQueryWithAdminReauth = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  if (!result.error || result.error.status !== 401) return result;
  if (SKIP_REFRESH_URLS.includes(args?.url)) return result;

  try {
    if (!adminRefreshPromise) {
      adminRefreshPromise = attemptAdminTokenRefresh().finally(() => {
        adminRefreshPromise = null;
      });
    }

    const refreshResult = await adminRefreshPromise;

    if (refreshResult?.loginToken) {
      setCookie(ADMIN_TOKEN_COOKIE_NAME, refreshResult.loginToken, ACCESS_TOKEN_AGE);
      if (refreshResult?.refreshToken) {
        setCookie(ADMIN_REFRESH_TOKEN_COOKIE_NAME, refreshResult.refreshToken, REFRESH_TOKEN_AGE);
      }
      Logger('Admin token refreshed — retrying request');
      return baseQuery(args, api, extraOptions);
    }
  } catch (error) {
    Logger('Admin token refresh failed', error);
  }

  Logger('Could not refresh admin token — logging out');
  handleAdminLogoutRedirect();
  return result;
};

const adminApiSlice = createApi({
  reducerPath: 'admin-api-slice',
  baseQuery: baseQueryWithAdminReauth,
  endpoints: () => ({}),
  keepUnusedDataFor: 0,
  refetchOnMountOrArgChange: true
});

export default adminApiSlice;
